import { api } from './client';
import type { Page } from './catalogs';
import type { CancellableState } from './cancellation';

// ==================== Asset Groups + Methods ====================

export type AssetKind = 'FIXED_ASSET' | 'INTANGIBLE' | 'LOW_VALUE';
export type AssetStatus =
  | 'PARKED' | 'OPERATING' | 'CONSERVATION'
  | 'REPAIR_LONG' | 'WRITTEN_OFF' | 'SOLD';
export type LowValueWriteOffMethod = 'AT_USE_100' | 'SPLIT_50_50';
export type LowValueTrackMode = 'INDIVIDUAL' | 'BATCH';
export type WriteOffReason = 'OBSOLETE' | 'BROKEN' | 'LOST' | 'OTHER';
export type TargetStatus = 'PARKED' | 'OPERATING';

export interface AssetGroup {
  id: string;
  code: string;
  asset_kind: AssetKind;
  group_number: number;
  name_uk: string;
  min_useful_life_months: number | null;
  accounting_account_code: string;
  depreciation_account_code: string | null;
  is_active: boolean;
  seed_source: string;
}

export interface DepreciationMethod {
  id: string;
  code: string;
  name_uk: string;
  description: string | null;
  is_active: boolean;
}

export const assetGroups = {
  list: (params?: { kind?: AssetKind; is_active?: boolean; limit?: number }) =>
    api.get<Page<AssetGroup>>('/refs/asset-groups', { params }).then((r) => r.data),
};

export const depreciationMethods = {
  list: (params?: { is_active?: boolean; limit?: number }) =>
    api.get<Page<DepreciationMethod>>('/refs/depreciation-methods', { params })
      .then((r) => r.data),
};

// ==================== Fixed Assets ====================

export interface FixedAsset {
  id: string;
  organization_id: string;
  inventory_number: string;
  name: string;
  asset_group_id: string;
  depreciation_method_id: string;
  primary_cost: string;
  liquidation_value: string;
  accumulated_depreciation: string;
  residual_value: string;
  useful_life_months: number;
  expense_account_code: string;
  ukt_zed_code?: string | null;
  commissioning_date: string | null;
  status: AssetStatus;
  department_id: string | null;
  responsible_employee_id: string | null;
  comment: string | null;
  tax_purpose_id?: string | null;
  /** ч.1388: податковий облік амортизації (п. 138.3). */
  tax_depreciable?: boolean;
  tax_useful_life_months?: number | null;
  tax_primary_cost?: string | null;
  tax_accumulated_depreciation?: string;
  tax_accelerated?: boolean;
  created_at: string;
  updated_at: string;
}

export interface FixedAssetCreate {
  organization_id: string;
  inventory_number?: string | null;
  name: string;
  asset_group_id: string;
  depreciation_method_id: string;
  primary_cost: string;
  liquidation_value?: string;
  useful_life_months: number;
  expense_account_code: string;
  /** 0447: override рахунків (порожньо = налаштування орг > група). */
  account_code?: string | null;
  depreciation_account_code?: string | null;
  ukt_zed_code?: string | null;
  department_id?: string | null;
  responsible_employee_id?: string | null;
  comment?: string | null;
  tax_depreciable?: boolean;
  tax_useful_life_months?: number | null;
  tax_primary_cost?: string | null;
  tax_accumulated_depreciation?: string;
  tax_accelerated?: boolean;
}

const FA_BASE = '/refs/fixed-assets';

export const fixedAssets = {
  list: (params?: { organization_id?: string; status?: string; limit?: number }) =>
    api.get<Page<FixedAsset>>(FA_BASE, { params }).then((r) => r.data),
  get: (id: string) => api.get<FixedAsset>(`${FA_BASE}/${id}`).then((r) => r.data),
  create: (p: FixedAssetCreate) => api.post<FixedAsset>(FA_BASE, p).then((r) => r.data),
  update: (id: string, p: Partial<FixedAssetCreate>) =>
    api.patch<FixedAsset>(`${FA_BASE}/${id}`, p).then((r) => r.data),
  remove: (id: string) => api.delete(`${FA_BASE}/${id}`).then(() => undefined),
};

// ==================== Intangible Assets ====================

export type IntangibleAsset = FixedAsset;
export interface IntangibleAssetCreate extends FixedAssetCreate {}

const IA_BASE = '/refs/intangible-assets';

export const intangibleAssets = {
  list: (params?: { organization_id?: string; status?: string; limit?: number }) =>
    api.get<Page<IntangibleAsset>>(IA_BASE, { params }).then((r) => r.data),
  get: (id: string) => api.get<IntangibleAsset>(`${IA_BASE}/${id}`).then((r) => r.data),
  create: (p: IntangibleAssetCreate) =>
    api.post<IntangibleAsset>(IA_BASE, p).then((r) => r.data),
  update: (id: string, p: Partial<IntangibleAssetCreate>) =>
    api.patch<IntangibleAsset>(`${IA_BASE}/${id}`, p).then((r) => r.data),
  remove: (id: string) => api.delete(`${IA_BASE}/${id}`).then(() => undefined),
};

// ==================== Low Value Assets ====================

export interface LowValueAsset {
  id: string;
  organization_id: string;
  inventory_number: string;
  name: string;
  primary_cost: string;
  write_off_method: LowValueWriteOffMethod;
  track_mode: LowValueTrackMode;
  quantity: number;
  cost_written_off: string;
  expense_account_code: string;
  ukt_zed_code?: string | null;
  commissioning_date: string | null;
  status: AssetStatus;
  department_id: string | null;
  responsible_employee_id: string | null;
  comment: string | null;
  tax_purpose_id?: string | null;
  created_at: string;
  updated_at: string;
}

export interface LowValueAssetCreate {
  organization_id: string;
  inventory_number?: string | null;
  name: string;
  primary_cost: string;
  write_off_method?: LowValueWriteOffMethod;
  track_mode?: LowValueTrackMode;
  quantity?: number;
  expense_account_code: string;
  /** 0447: рахунок обліку активу (порожньо = налаштування орг > 22). */
  account_code?: string | null;
  ukt_zed_code?: string | null;
  department_id?: string | null;
  responsible_employee_id?: string | null;
  comment?: string | null;
}

const LV_BASE = '/refs/low-value-assets';

export const lowValueAssets = {
  list: (params?: { organization_id?: string; limit?: number }) =>
    api.get<Page<LowValueAsset>>(LV_BASE, { params }).then((r) => r.data),
  get: (id: string) => api.get<LowValueAsset>(`${LV_BASE}/${id}`).then((r) => r.data),
  create: (p: LowValueAssetCreate) =>
    api.post<LowValueAsset>(LV_BASE, p).then((r) => r.data),
  update: (id: string, p: Partial<LowValueAssetCreate>) =>
    api.patch<LowValueAsset>(`${LV_BASE}/${id}`, p).then((r) => r.data),
  remove: (id: string) => api.delete(`${LV_BASE}/${id}`).then(() => undefined),
};

// ==================== Asset Commissioning ====================

export interface AssetCommissioningItem {
  id?: string;
  line_number: number;
  asset_kind: AssetKind;
  fixed_asset_id?: string | null;
  intangible_asset_id?: string | null;
  low_value_asset_id?: string | null;
  primary_cost_snapshot: string;
  useful_life_months_snapshot?: number | null;
}

export interface AssetCommissioning {
  id: string;
  organization_id: string;
  document_number: string;
  document_date: string;
  posted: boolean;
  posted_at: string | null;
  deletion_mark: boolean;
  comment: string | null;
  tax_purpose_id?: string | null;
  target_status: TargetStatus;
  external_source?: string | null;
  items: AssetCommissioningItem[];
  created_at: string;
  updated_at: string;
  // Block 5.3c:
  state?: CancellableState;
  cancelled_at?: string | null;
  cancellation_reason?: string | null;
}

export interface AssetCommissioningCreate {
  organization_id: string;
  document_number?: string | null;
  document_date: string;
  target_status?: TargetStatus;
  comment?: string | null;
  items: AssetCommissioningItem[];
}

const AC_BASE = '/documents/asset-commissioning';

export const assetCommissioning = {
  list: (params?: { organization_id?: string; posted?: boolean; date_from?: string; date_to?: string; limit?: number }) =>
    api.get<Page<AssetCommissioning>>(AC_BASE, { params }).then((r) => r.data),
  get: (id: string) =>
    api.get<AssetCommissioning>(`${AC_BASE}/${id}`).then((r) => r.data),
  create: (p: AssetCommissioningCreate) =>
    api.post<AssetCommissioning>(AC_BASE, p).then((r) => r.data),
  update: (id: string, p: Partial<AssetCommissioningCreate>) =>
    api.patch<AssetCommissioning>(`${AC_BASE}/${id}`, p).then((r) => r.data),
  post: (id: string) =>
    api.post<AssetCommissioning>(`${AC_BASE}/${id}/post`).then((r) => r.data),
  unpost: (id: string, force = false) =>
    api.post<AssetCommissioning>(`${AC_BASE}/${id}/unpost`, null, { params: force ? { force: true } : {} }).then((r) => r.data),
  cancel: (id: string, reason: string) =>
    api.post(`${AC_BASE}/${id}/cancel`, { cancellation_reason: reason }).then((r) => r.data),
  remove: (id: string) => api.delete(`${AC_BASE}/${id}`).then(() => undefined),
};

// ==================== Asset Write-off ====================

export interface AssetWriteOffItem {
  id?: string;
  line_number: number;
  asset_kind: AssetKind;
  fixed_asset_id?: string | null;
  intangible_asset_id?: string | null;
  low_value_asset_id?: string | null;
  primary_cost_snapshot: string;
  accumulated_depreciation_snapshot?: string;
}

export interface AssetWriteOff {
  id: string;
  organization_id: string;
  document_number: string;
  document_date: string;
  posted: boolean;
  posted_at: string | null;
  deletion_mark: boolean;
  comment: string | null;
  tax_purpose_id?: string | null;
  reason: WriteOffReason;
  reason_details: string | null;
  source_inventory_id: string | null;
  external_source?: string | null;
  items: AssetWriteOffItem[];
  created_at: string;
  updated_at: string;
  // Block 5.3c:
  state?: CancellableState;
  cancelled_at?: string | null;
  cancellation_reason?: string | null;
}

export interface AssetWriteOffCreate {
  organization_id: string;
  document_number?: string | null;
  document_date: string;
  reason: WriteOffReason;
  reason_details?: string | null;
  source_inventory_id?: string | null;
  comment?: string | null;
  items: AssetWriteOffItem[];
}

const WO_BASE = '/documents/asset-write-off';

export const assetWriteOff = {
  list: (params?: { organization_id?: string; posted?: boolean; date_from?: string; date_to?: string; limit?: number }) =>
    api.get<Page<AssetWriteOff>>(WO_BASE, { params }).then((r) => r.data),
  get: (id: string) => api.get<AssetWriteOff>(`${WO_BASE}/${id}`).then((r) => r.data),
  create: (p: AssetWriteOffCreate) =>
    api.post<AssetWriteOff>(WO_BASE, p).then((r) => r.data),
  update: (id: string, p: Partial<AssetWriteOffCreate>) =>
    api.patch<AssetWriteOff>(`${WO_BASE}/${id}`, p).then((r) => r.data),
  post: (id: string) =>
    api.post<AssetWriteOff>(`${WO_BASE}/${id}/post`).then((r) => r.data),
  unpost: (id: string) =>
    api.post<AssetWriteOff>(`${WO_BASE}/${id}/unpost`).then((r) => r.data),
  cancel: (id: string, reason: string) =>
    api.post(`${WO_BASE}/${id}/cancel`, { cancellation_reason: reason }).then((r) => r.data),
  remove: (id: string) => api.delete(`${WO_BASE}/${id}`).then(() => undefined),
};


// ==================== 0447: Рахунки обліку НА ====================

export interface AssetGroupOverride {
  asset_group_id: string;
  account_code: string | null;
  depreciation_account_code: string | null;
}
export interface AssetAccountSettings {
  lv_account_code: string | null;
  lv_expense_account_code: string | null;
  group_overrides: AssetGroupOverride[];
}
export interface NonstandardAssetAccountRow {
  kind: string; id: string; inventory_number: string; name: string;
  field: 'account' | 'depreciation' | 'expense';
  card_code: string; default_code: string | null; external_source: string | null;
}
export const assetAccountSettings = {
  get: (organization_id: string) =>
    api.get<AssetAccountSettings>('/refs/asset-account-settings', { params: { organization_id } }).then((r) => r.data),
  put: (p: AssetAccountSettings & { organization_id: string }) =>
    api.put<AssetAccountSettings>('/refs/asset-account-settings', p).then((r) => r.data),
  nonstandard: (organization_id: string, params?: { kind?: string; limit?: number; offset?: number }) =>
    api.get<{ items: NonstandardAssetAccountRow[]; total: number; limit: number; offset: number }>(
      '/refs/asset-account-settings/nonstandard', { params: { organization_id, ...params } }).then((r) => r.data),
};
